import { useState } from 'react';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Input, Textarea } from '@nextui-org/react';
import { checkType, CopyCodeField, CtyButton, CtyShowToast, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { useCreateCredentialMutation, type CredentialPayload } from '@circuitry-ai/doc-data';
import { useTranslation } from 'react-i18next';

export interface WebAgentCredentialModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Receives the code of the credential just created, so the caller can select it once refetched. */
  readonly onCreated: (code: string) => void;
}

const fieldLabel = 'flex items-center text-sm pb-1 font-medium';

/**
 * Creates the one credential shape a web agent step can use: a `key_value` secret holding a single
 * string. Field layout follows the Settings credential form, minus the options a step value can never
 * reference (OAuth token URLs, grant types, TTLs) — the type is always `key_value`.
 */
export function WebAgentCredentialModal({ isOpen, onClose, onCreated }: WebAgentCredentialModalProps) {
  const { t } = useTranslation();
  const [createCredential, { isLoading }] = useCreateCredentialMutation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');

  const isValid = Boolean(name.trim() && key.trim() && value);

  const handleClose = () => {
    setCode('');
    setName('');
    setDescription('');
    setKey('');
    setValue('');
    onClose();
  };

  const handleCreate = async () => {
    if (!isValid) return;
    const trimmedCode = code.trim();
    // `code` and `config.value_type` are accepted by the API but missing from CredentialPayload.
    const payload = {
      name: name.trim(),
      description: description.trim(),
      credential_type: 'key_value',
      ...optionalCodePayload(code),
      config: { key: key.trim(), value_type: 'string' },
      secrets: { value },
      tags: [],
    } as CredentialPayload;

    try {
      const response = await createCredential(payload).unwrap();
      CtyShowToast(checkType.success, t('Credential created.'));
      const createdCode = (response as { data?: { code?: string }; code?: string })?.data?.code
        ?? (response as { code?: string })?.code
        ?? trimmedCode;
      onCreated(createdCode);
      handleClose();
    } catch (error: any) {
      CtyShowToast(checkType.error, error?.data?.message ?? error?.message ?? t('Failed to create the credential.'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isDismissable={!isLoading} scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="text-base">{t('New credential')}</ModalHeader>
        <ModalBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CopyCodeField label="Code" value={code} className="md:col-span-2" isRequired={false} onValueChange={setCode} />

            <div className="md:col-span-2">
              <label className={fieldLabel} htmlFor="web-agent-credential-name">
                {t('Credential Name')}<span className="text-danger">*</span>
              </label>
              <Input id="web-agent-credential-name" size="sm" autoFocus placeholder={t('Enter credential name')} value={name} onValueChange={setName} />
            </div>

            <div className="md:col-span-2">
              <label className={fieldLabel} htmlFor="web-agent-credential-description">{t('Description')}</label>
              <Textarea
                id="web-agent-credential-description"
                size="sm"
                minRows={3}
                maxRows={6}
                placeholder={t('Enter description')}
                classNames={{ input: 'resize-y' }}
                value={description}
                onValueChange={setDescription}
              />
            </div>

            <div>
              <label className={fieldLabel} htmlFor="web-agent-credential-key">
                {t('Key')}<span className="text-danger">*</span>
              </label>
              <Input id="web-agent-credential-key" size="sm" placeholder={t('Key')} value={key} onValueChange={setKey} />
            </div>

            <div>
              <label className={fieldLabel} htmlFor="web-agent-credential-value">
                {t('Value')}<span className="text-danger">*</span>
              </label>
              <Input id="web-agent-credential-value" size="sm" type="password" placeholder={t('Enter value')} value={value} onValueChange={setValue} />
            </div>
          </div>
          <p className="text-xs text-default-500">
            {t('Stored as a secret. Steps reference it by name — the value is only read when the agent runs.')}
          </p>
        </ModalBody>
        <ModalFooter>
          <CtyButton size="sm" variant="bordered" onPress={handleClose} isDisabled={isLoading}>{t('Cancel')}</CtyButton>
          <CtyButton size="sm" color="primary" onPress={handleCreate} isLoading={isLoading} isDisabled={!isValid}>
            {t('Create')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default WebAgentCredentialModal;
